"use client";

import Script from "next/script";
import { useId, useRef, useState } from "react";
import { contactTopics } from "./contact-form";

type FormState = {
  status: "idle" | "submitting" | "success" | "error";
  message: string;
};

const contactRequestTimeoutMs = 60000;
const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

declare global {
  interface Window {
    turnstile?: {
      reset: (container?: string | HTMLElement) => void;
    };
  }
}

export function ContactFormClient() {
  const [state, setState] = useState<FormState>({ status: "idle", message: "" });
  const turnstileId = useId();
  const turnstileRef = useRef<HTMLDivElement>(null);

  function resetTurnstile() {
    try {
      if (turnstileRef.current && window.turnstile) {
        window.turnstile.reset(turnstileRef.current);
      }
    } catch {
      // The message has already been handled; a widget reset failure should not change the submission result.
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ status: "submitting", message: "" });

    const form = event.currentTarget;
    const formData = new FormData(form);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), contactRequestTimeoutMs);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.get("email"),
          topic: formData.get("topic"),
          message: formData.get("message"),
          turnstileToken: formData.get("cf-turnstile-response"),
          website: formData.get("website"),
        }),
      });

      const result = (await response.json()) as { error?: string };

      if (!response.ok) {
        setState({ status: "error", message: result.error ?? "Message could not be sent." });
        return;
      }

      form.reset();
      resetTurnstile();
      setState({
        status: "success",
        message: "We have received your request, a customer representative will contact you shortly, thank you.",
      });
    } catch (error) {
      const isTimeout = error instanceof DOMException && error.name === "AbortError";

      setState({
        status: "error",
        message: isTimeout
          ? "The request is taking longer than expected. Please wait a moment before trying again."
          : "Message could not be sent. Please email sales@esited.com.",
      });
    } finally {
      window.clearTimeout(timeout);
      resetTurnstile();
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <label>
        Email address
        <input name="email" type="email" placeholder="you@example.com" autoComplete="email" required />
      </label>
      <label>
        Topic
        <select name="topic" defaultValue="Sales Inquiry" required>
          {contactTopics.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </label>
      <label>
        Message
        <textarea name="message" placeholder="How can Esited help?" rows={6} required></textarea>
      </label>
      <div className="contact-captcha">
        {turnstileSiteKey ? (
          <>
            <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer />
            <div
              id={turnstileId}
              ref={turnstileRef}
              className="cf-turnstile"
              data-sitekey={turnstileSiteKey}
              data-theme="auto"
            />
          </>
        ) : (
          <p className="contact-form-status contact-form-status-error" role="status">
            Security check is not configured.
          </p>
        )}
      </div>
      <label className="contact-honeypot" aria-hidden="true">
        Website
        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
      </label>
      <button className="btn primary" type="submit" disabled={state.status === "submitting" || !turnstileSiteKey}>
        {state.status === "submitting" ? "Sending..." : "Submit"}
      </button>
      {state.message ? (
        <p className={`contact-form-status contact-form-status-${state.status}`} role="status">
          {state.message}
        </p>
      ) : null}
    </form>
  );
}
